import {generateRandomId, MediaDetection, MediaDetectionType} from '@legit-health/ui';
import type {AihsDetection} from '../hooks/useAihs4SeverityAssessment';

/**
 * Maps the detection type string returned by the AIHS4 endpoint
 * (`code.text` — e.g. "Nodule", "Abscess", "Draining tunnel") to the
 * corresponding `MediaDetectionType` used everywhere in the UI. Unknown
 * types fall back to `Lesion` so the user can still see and edit the box.
 */
function mapCodeToDetectionType(codeText: string): MediaDetectionType {
  const normalized = codeText.trim().toLowerCase();
  if (normalized === 'nodule') {
    return MediaDetectionType.Nodule;
  }
  if (normalized === 'abscess') {
    return MediaDetectionType.Abscess;
  }
  if (normalized === 'draining tunnel' || normalized === 'drainingtunnel') {
    return MediaDetectionType.DrainingTunnel;
  }
  return MediaDetectionType.Lesion;
}

/**
 * Converts a detection coming from the AIHS4 endpoint (with {x1,y1,x2,y2}
 * pixel coordinates in the original image) to a `MediaDetection` compatible
 * with `DetectionCanvas`. An id is generated locally because the endpoint
 * does not return one.
 */
export default function apiDetectionToMediaDetection(detection: AihsDetection): MediaDetection {
  const {x1, y1, x2, y2} = detection.box;
  return {
    id: generateRandomId(),
    type: mapCodeToDetectionType(detection.code.text),
    rect: {
      topLeft: {x: x1, y: y1},
      width: Math.max(0, x2 - x1),
      height: Math.max(0, y2 - y1)
    }
  };
}
